import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Users, Lock, Heart } from 'lucide-react';

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-dark">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/a344fa32-7d0c-4e1a-b46f-52aab072ca7d/images/dffa004619c6bc6ebd0632353e4b1e499b8f9291d61e9f9c120f05f41c0c5478.png)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-dark/70 via-dark/50 to-dark"></div>
        </div>
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 text-center">
          <div className="animate-fade-in" style={{ animationDelay: '0.1s' }}>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl mb-6 tracking-tight">
              <span className="gold-gradient-text">DiscreetLux</span>
            </h1>
          </div>
          
          <div className="animate-fade-in" style={{ animationDelay: '0.3s' }}>
            <p className="font-accent text-2xl sm:text-3xl lg:text-4xl text-gray-300 mb-4 italic">
              Des rencontres très discrètes
            </p>
          </div>
          
          <div className="animate-fade-in" style={{ animationDelay: '0.5s' }}>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-12 leading-relaxed">
              La sélection des membres se fait à l'entrée par la communauté.<br />
              <span className="text-gold">En ferez-vous partie ?</span>
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in" style={{ animationDelay: '0.6s' }}>
            <Link 
              to="/register" 
              className="btn-gold flex items-center gap-2 w-full sm:w-auto justify-center"
              data-testid="hero-register-btn"
            >
              Rejoindre la communauté
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link 
              to="/login" 
              className="btn-outline-gold w-full sm:w-auto"
              data-testid="hero-login-btn"
            >
              Se connecter
            </Link>
          </div>
          
          {/* Slogan after buttons */}
          <div className="mt-8 md:mt-12 animate-fade-in" style={{ animationDelay: '0.8s' }}>
            <p className="font-accent text-lg sm:text-xl md:text-2xl tracking-wide mb-3" style={{ color: '#D4AF37' }}>
              Certains désirs ne supportent pas la lumière.
            </p>
            <p className="text-gray-400 text-xs sm:text-sm max-w-2xl mx-auto leading-relaxed px-4">
              Site de rencontre pour couples, femmes et hommes axé sur la discrétion, le luxe subtil et la confidentialité, destiné à des adultes consentants recherchant des connexions raffinées et respectueuses.
            </p>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-gold/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-gold rounded-full animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-dark-paper relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-serif text-3xl sm:text-4xl text-center mb-4">
            <span className="gold-gradient-text">Une expérience exclusive</span>
          </h2>
          <p className="text-gray-400 text-center max-w-2xl mx-auto mb-16">
            DiscreetLux offre un espace de rencontres haut de gamme pour les personnes exigeantes
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <FeatureCard 
              icon={<Shield className="w-8 h-8" />}
              title="Validation communautaire"
              description="Chaque nouveau membre est validé par 5 membres existants"
            />
            <FeatureCard 
              icon={<Lock className="w-8 h-8" />}
              title="Discrétion absolue"
              description="Vos photos privées ne sont visibles qu'aux personnes autorisées"
            />
            <FeatureCard 
              icon={<Users className="w-8 h-8" />}
              title="Membres certifiés"
              description="Possibilité de certification par notre équipe"
            />
            <FeatureCard 
              icon={<Heart className="w-8 h-8" />}
              title="Rencontres authentiques"
              description="Une communauté engagée et respectueuse"
            />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 bg-dark relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-serif text-3xl sm:text-4xl text-center mb-4">
            <span className="gold-gradient-text">Comment ça marche ?</span>
          </h2>
          <p className="text-gray-400 text-center max-w-2xl mx-auto mb-16">
            Un processus simple pour rejoindre notre communauté exclusive
          </p>
          
          <div className="grid md:grid-cols-3 gap-12">
            <StepCard 
              number="01"
              title="Créez votre profil"
              description="Remplissez votre profil avec au minimum 2 photos élégantes et suggestives."
            />
            <StepCard 
              number="02"
              title="Validation"
              description="5 membres abonnés examinent et valident votre profil pour garantir la qualité de la communauté."
            />
            <StepCard 
              number="03"
              title="Explorez"
              description="Une fois certifié, accédez au fil d'actualité. Abonnez-vous pour débloquer toutes les fonctionnalités."
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-dark-paper relative">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/5594992/pexels-photo-5594992.jpeg)',
          }}
        />
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mb-6">
            <span className="gold-gradient-text">Prêt à nous rejoindre ?</span>
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Rejoignez une communauté où l'élégance rencontre la discrétion
          </p>
          <Link 
            to="/register" 
            className="btn-gold inline-flex items-center gap-2"
            data-testid="cta-register-btn"
          >
            Commencer maintenant
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-dark border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gold to-gold-dark flex items-center justify-center">
                <span className="text-dark font-serif text-sm">D</span>
              </div>
              <span className="font-serif text-xl gold-gradient-text">DiscreetLux</span>
            </div>
            <div className="flex items-center gap-8 text-sm text-gray-500">
              <Link to="/cgu" className="hover:text-gold transition-colors">Conditions Générales d'Utilisation</Link>
              <Link to="/privacy" className="hover:text-gold transition-colors">Confidentialité</Link>
              <Link to="/contact" className="hover:text-gold transition-colors">Contact</Link>
            </div>
            <p className="text-gray-500 text-sm">
              © 2024 DiscreetLux. Tous droits réservés.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

const FeatureCard = ({ icon, title, description }) => (
  <div className="glass-card p-8 text-center group hover:scale-105 transition-transform duration-500" data-testid="feature-card">
    <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gold/10 flex items-center justify-center text-gold group-hover:bg-gold/20 transition-colors duration-300">
      {icon}
    </div>
    <h3 className="font-serif text-xl text-white mb-3">{title}</h3>
    <p className="text-gray-400 text-sm leading-relaxed">{description}</p>
  </div>
);

const StepCard = ({ number, title, description }) => (
  <div className="relative" data-testid="step-card">
    <div className="text-7xl font-serif text-gold/10 absolute -top-4 left-0">{number}</div>
    <div className="pt-12 pl-4">
      <h3 className="font-serif text-xl text-white mb-3">{title}</h3>
      <p className="text-gray-400 text-sm leading-relaxed">{description}</p>
    </div>
  </div>
);
